export type WaitForTourTargetOptions = {
	requireEnabled?: boolean
	/** Dev warning threshold in ms; null = no warning. Polls until ready regardless. */
	timeoutMs?: number | null
}

export function isTourTargetVisible(el: Element): boolean {
	if (!(el instanceof HTMLElement)) return false
	if (el.hidden) return false

	const style = window.getComputedStyle(el)
	if (style.display === 'none' || style.visibility === 'hidden') return false
	if (Number.parseFloat(style.opacity) === 0) return false

	const rect = el.getBoundingClientRect()
	const hasSize = rect.width > 0 || rect.height > 0 || el.offsetWidth > 0 || el.offsetHeight > 0
	if (!hasSize) return false

	return true
}

export function isTourTargetEnabled(el: Element): boolean {
	if (
		el instanceof HTMLButtonElement ||
		el instanceof HTMLInputElement ||
		el instanceof HTMLSelectElement ||
		el instanceof HTMLTextAreaElement ||
		el instanceof HTMLOptionElement ||
		el instanceof HTMLFieldSetElement
	) {
		if (el.disabled) return false
	}

	if (el.getAttribute('aria-disabled') === 'true') return false

	return true
}

function queryTourTargetReady(selector: string, requireEnabled: boolean): Element | null {
	const el = document.querySelector(selector)
	if (!el) return null
	if (!isTourTargetVisible(el)) return null
	if (requireEnabled && !isTourTargetEnabled(el)) return null
	return el
}

export function waitForTourTarget(
	selector: string,
	opts?: WaitForTourTargetOptions
): Promise<void> {
	const requireEnabled = opts?.requireEnabled ?? false
	const warnAfterMs = opts?.timeoutMs === undefined ? 30_000 : opts.timeoutMs

	return new Promise((resolve) => {
		if (queryTourTargetReady(selector, requireEnabled)) {
			resolve()
			return
		}

		let warned = false
		const start = Date.now()

		const tick = () => {
			if (queryTourTargetReady(selector, requireEnabled)) {
				resolve()
				return
			}

			if (warnAfterMs !== null && !warned && Date.now() - start > warnAfterMs) {
				warned = true
				if (import.meta.env.DEV) {
					console.warn(`[product-tour] Still waiting for tour target: ${selector}`)
				}
			}

			requestAnimationFrame(tick)
		}

		requestAnimationFrame(tick)
	})
}

function resolveWaitOptions(timeoutMs?: number): WaitForTourTargetOptions {
	if (timeoutMs === 120_000 || timeoutMs === 240_000) {
		return { timeoutMs: null }
	}
	if (timeoutMs !== undefined) {
		return { timeoutMs: timeoutMs }
	}
	return {}
}

/** @deprecated Prefer waitForTourTarget. Kept for existing call sites. */
export function waitForElement(selector: string, timeoutMs?: number) {
	return waitForTourTarget(selector, resolveWaitOptions(timeoutMs))
}

export async function waitForElementAndScroll(
	selector: string,
	opts?: number | WaitForTourTargetOptions
) {
	const waitOpts = typeof opts === 'number' ? resolveWaitOptions(opts) : (opts ?? {})
	await waitForTourTarget(selector, waitOpts)
	const el = document.querySelector(selector)
	if (el instanceof HTMLElement) {
		el.scrollIntoView({ behavior: 'smooth', block: 'center' })
	}
}

export async function waitForTourTargets(
	selectors: Array<{ selector: string; requireEnabled?: boolean }>,
	opts?: WaitForTourTargetOptions
) {
	for (const { selector, requireEnabled } of selectors) {
		await waitForTourTarget(selector, { ...opts, requireEnabled: requireEnabled ?? false })
	}
}
